import { Link, createFileRoute } from "@tanstack/react-router";
import {
  closeConversation,
  getTicketNotificationCount,
  getUserTickets,
  ticketNotificationsCountQueryKey,
} from "@/queries/tickets";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { useEffect, useRef, useState } from "react";
import { TicketStatus } from "@/types";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Bot,
  Check,
  Clock,
  Loader2,
  LockKeyhole,
  MessageCircle,
  Pin,
  Plus,
  X,
} from "lucide-react";
import { DataTable } from "@/components/ui/data-table";
import type { ColumnDef, PaginationState } from "@tanstack/react-table";
import { ticketOptions } from "./create";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/support/tickets/")({
  component: RouteComponent,
});

function RouteComponent() {
  const [meta, setMeta] = useState<any>({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [tickets, setTickets] = useState<any>([]);
  const [closeModal, setCloseModal] = useState(false);
  const [rowSelection, setRowSelection] = useState<any>({});
  const [visibility, setVisibility] = useState<any>({});

  const ticketId = useRef(0);

  const ticketsQuery = useQuery({
    queryFn: () =>
      getUserTickets(pagination.pageIndex + 1, pagination.pageSize),
    queryKey: ["user", "tickets", pagination],
  });

  // تعداد پیام‌های خوانده‌نشده‌ای که ادمین/ربات ایساج فرستاده‌اند
  // (queryKey مشترک با سایدبار است تا درخواست تکراری ارسال نشود)
  const notificationCountQuery = useQuery({
    queryKey: ticketNotificationsCountQueryKey,
    queryFn: getTicketNotificationCount,
  });

  const unreadTicketCount = notificationCountQuery.data ?? 0;

  useEffect(() => {
    if (!ticketsQuery.isFetching) {
      const data = ticketsQuery.data?.data;
      const ticketList = Array.isArray(data?.data) ? data.data : [];

      // Keep the Esaj bot conversation pinned while preserving the API order
      // of all other tickets.
      setTickets(
        [...ticketList].sort(
          (a, b) => Number(Boolean(b.is_esaj)) - Number(Boolean(a.is_esaj)),
        ),
      );
      setMeta(data?.meta ?? {});
    }
  }, [ticketsQuery.data, ticketsQuery.isFetching]);

  const closeModalMutation = useMutation({
    mutationFn: () => closeConversation(ticketId.current),
    mutationKey: ["close", "conversation", ticketId.current],
    onSuccess() {
      setTickets((t: any) =>
        t.map((s: any) => {
          if (s.id == ticketId.current) {
            s.status = "closed";
          }
          return s;
        }),
      );
      setCloseModal(false);
      toast.success("تیکت با موفقیت بسته شد");
    },
    onError() {
      toast.error("خطایی در بستن تیکت رخ داده است");
    },
  });

  const confirmClose = (id: number) => {
    setCloseModal(true);
    ticketId.current = id;
  };

  const statusStyles = {
    answering:
      "border-sky-300 bg-sky-100 text-sky-800 dark:border-sky-700 dark:bg-sky-900/60 dark:text-sky-200",
    answered:
      "border-emerald-300 bg-emerald-100 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-200",
    closed:
      "border-red-300 bg-red-100 text-red-800 dark:border-red-700 dark:bg-red-900/60 dark:text-red-200",
  };

  const statusIcon = {
    answering: <Clock className="size-3" />,
    answered: <Check className="size-3" />,
    closed: <X className="size-3" />,
  };

  const columns: ColumnDef<any, any>[] = [
    {
      id: "ticket",
      header: "تیکت",
      cell: ({ row }) => {
        const ticket = row.original;
        const ticketOption = ticketOptions.find(
          (option) => option.value == ticket.category,
        );
        const OptionIcon = ticketOption?.icon;
        const date = new Date(ticket.created_at);

        return (
          <Link
            to="/support/tickets/$id"
            params={{ id: String(ticket.id) }}
            className="group relative flex w-full items-center gap-3 rounded-xl px-3 py-3 text-right transition-colors hover:bg-muted/60"
          >
            <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              {ticket.is_esaj ? (
                <Bot className="size-5" />
              ) : OptionIcon ? (
                <OptionIcon className="size-5" />
              ) : (
                <MessageCircle className="size-5" />
              )}
            </div>

            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-1.5 text-sm font-medium">
                  <span className="truncate">
                    {ticket.title ||
                      (ticket.is_esaj ? "ربات ایساج" : `تیکت #${ticket.id}`)}
                  </span>
                  {ticket.is_esaj && (
                    <Pin
                      className="size-3.5 shrink-0 rotate-[-35deg] text-primary"
                      aria-label="گفت‌وگوی پین‌شده"
                    />
                  )}
                </span>
                <Badge
                  className={`shrink-0 gap-1 text-[11px] ${statusStyles[ticket.status as keyof typeof TicketStatus]}`}
                >
                  {statusIcon[ticket.status as keyof typeof TicketStatus]}
                  {TicketStatus[ticket.status as keyof typeof TicketStatus]}
                </Badge>
              </div>
              <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span className="truncate">
                  {ticketOption?.label || "-"} · #{ticket.id}
                </span>
                <span dir="ltr" className="shrink-0 tabular-nums">
                  {Intl.DateTimeFormat("fa-ir", { dateStyle: "short" }).format(
                    date,
                  )}
                </span>
              </div>
            </div>

            {ticket.status !== "closed" && (
              <Button
                size="icon"
                variant="ghost"
                className="size-8 shrink-0 rounded-full opacity-0 transition-opacity group-hover:opacity-100"
                aria-label="بستن تیکت"
                title="بستن تیکت"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  confirmClose(ticket.id);
                }}
              >
                <LockKeyhole className="size-4" />
              </Button>
            )}
          </Link>
        );
      },
    },
  ];

  return (
    <>
      <Transition appear show={closeModal}>
        <Dialog
          as="div"
          className="relative z-10 focus:outline-none"
          onClose={() => setCloseModal(false)}
        >
          <div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm"
            aria-hidden="true"
          />
          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <TransitionChild
                enter="ease-out duration-300"
                enterFrom="opacity-0 transform-[scale(95%)]"
                enterTo="opacity-100 transform-[scale(100%)]"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 transform-[scale(100%)]"
                leaveTo="opacity-0 transform-[scale(95%)]"
              >
                <DialogPanel className="w-full max-w-sm rounded-2xl border border-border/60 bg-background p-6 text-foreground shadow-lg">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="flex size-10 items-center justify-center rounded-full bg-red-100 text-red-700 dark:bg-red-900/60 dark:text-red-200">
                      <LockKeyhole className="size-5" />
                    </div>
                    <DialogTitle as="h3" className="text-base font-medium">
                      بستن مکالمه
                    </DialogTitle>
                  </div>

                  <p className="text-sm text-muted-foreground">
                    آیا از بستن این تیکت اطمینان دارید؟
                  </p>

                  <div className="mt-5 flex justify-end gap-2">
                    <Button
                      onClick={() => setCloseModal(false)}
                      variant="outline"
                    >
                      لغو
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => closeModalMutation.mutate()}
                    >
                      {closeModalMutation.isPending ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <span>تایید</span>
                      )}
                    </Button>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>

      <Card className="border-border/60 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardHeader className="p-3 lg:p-6">
            <CardTitle className="flex items-center gap-3 text-base lg:text-2xl">
              <span className="relative flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <MessageCircle className="size-5" />
                {unreadTicketCount > 0 && (
                  <span className="absolute -left-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold leading-none text-white shadow-sm">
                    {Intl.NumberFormat("fa-ir").format(unreadTicketCount)}
                  </span>
                )}
              </span>
              تیکت‌ها
            </CardTitle>
          </CardHeader>
          <div className="m-3 flex lg:m-4">
            <Link to="/support/tickets/create">
              <Button className="rounded-full">
                ایجاد تیکت جدید
                <Plus className="mr-2 size-4" />
              </Button>
            </Link>
          </div>
        </div>

        <CardContent className="p-2 pt-0 lg:p-6 lg:pt-0">
          <div className="[&_table]:table-fixed [&_thead]:hidden [&_td]:border-0 [&_td]:p-0 [&_tr]:border-0">
            <DataTable
              columnVisibility={visibility}
              onColumnVisibilityChange={setVisibility}
              rowSelection={rowSelection}
              onRowSelectionChange={setRowSelection}
              columns={columns}
              data={tickets}
              pageCount={meta.last_page}
              isLoading={ticketsQuery.isFetching}
              pagination={pagination}
              onPaginationChange={setPagination}
            />
          </div>
        </CardContent>
      </Card>
    </>
  );
}
